/**
 * RefineChipBar — chip-input replacement for the Cogni results table's
 * free-text "Refine" box. Styled like SearchChipBar (see that file for the
 * chip/dropdown Tailwind classes this mirrors), but suggestions are built
 * CLIENT-SIDE from the currently loaded result rows (`baseRows`) via
 * `suggestRefineChips` in utils/refineQuery.ts — no backend call, no
 * debounce needed (pure, local). Controlled: `chips` / `onChipsChange`.
 *
 * Typed-but-uncommitted text does NOT filter the table — only committed
 * chips do (approved behaviour change from the old free-text matcher).
 */
import React, { useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import {
  REFINE_KIND_LABELS,
  formatRefineChipValue,
  suggestRefineChips,
  type RefineChip,
  type RefineFields,
  type RefineSuggestion,
} from '../utils/refineQuery';

interface RefineChipBarProps {
  chips: RefineChip[];
  onChipsChange: (chips: RefineChip[]) => void;
  /** Rows passing every OTHER filter (Cogni: region/approval-year/last-updated
   *  column filters via TanStack's faceted row model; Keyword/Instant: every
   *  row on the current page) — see refineQuery.ts's suggestRefineChips doc. */
  baseRows: RefineFields[];
  /** Compact mode: smaller placeholder/padding, used in sidebar usage. */
  compact?: boolean;
  /** Overrides the default (Cogni) placeholder text when chips are empty. */
  placeholder?: string;
  /** Overrides the input's accessible name (defaults to Cogni's "Refine results"). */
  ariaLabel?: string;
  /** Region chips read "US-DailyMed"/"US-Drugs@FDA" when US-Drugs@FDA is visible. */
  usFdaVisible?: boolean;
}

function chipKey(chip: RefineChip): string {
  return `${chip.kind}:${chip.value}`;
}

const RefineChipBar: React.FC<RefineChipBarProps> = ({
  chips,
  onChipsChange,
  baseRows,
  compact = false,
  placeholder: placeholderProp,
  ariaLabel = 'Refine results',
  usFdaVisible = false,
}) => {
  const [text, setText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = useMemo(
    () => suggestRefineChips(text, baseRows, chips),
    [text, baseRows, chips],
  );
  // Clamped at read time: suggestions can shrink (e.g. baseRows changes)
  // without the text changing.
  const active = Math.min(activeIndex, Math.max(suggestions.length - 1, 0));

  const addChip = (suggestion: RefineSuggestion) => {
    onChipsChange([...chips, { kind: suggestion.kind, value: suggestion.value }]);
    setText('');
    setActiveIndex(0);
    setIsOpen(false);
  };

  const removeChip = (target: RefineChip) => {
    onChipsChange(chips.filter((c) => chipKey(c) !== chipKey(target)));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = suggestions[active];
      if (target) {
        addChip(target);
      }
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (suggestions.length === 0) return;
      e.preventDefault();
      setIsOpen(true);
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((active + step + suggestions.length) % suggestions.length);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Backspace' && text === '' && chips.length > 0) {
      e.preventDefault();
      onChipsChange(chips.slice(0, -1));
    }
  };

  const showDropdown = isOpen && text.trim() !== '';
  const defaultPlaceholder = compact ? 'Refine...' : 'Refine by brand, generic, manufacturer, region, year or text...';
  const placeholder = chips.length === 0 ? (placeholderProp ?? defaultPlaceholder) : '';

  return (
    <div className={`relative flex-1 ${compact ? 'min-w-[150px]' : 'min-w-[320px]'}`} data-testid="refine-chip-bar">
      <div
        className={`flex flex-wrap items-center gap-1.5 w-full rounded-lg border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent ${compact ? 'pl-7 pr-2 py-1' : 'pl-9 pr-3 py-2'}`}
        onClick={() => inputRef.current?.focus()}
      >
        <Search className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${compact ? 'left-2.5 w-3 h-3' : 'left-3 w-4 h-4'}`} />
        {chips.map((chip) => (
          <span
            key={chipKey(chip)}
            data-testid="refine-chip"
            className={`inline-flex items-center gap-1 font-medium rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 ${compact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'}`}
          >
            <span className="text-indigo-400">{REFINE_KIND_LABELS[chip.kind]}:</span>
            {formatRefineChipValue(chip, usFdaVisible)}
            <button
              type="button"
              aria-label={`Remove ${REFINE_KIND_LABELS[chip.kind]}: ${chip.value}`}
              onClick={(e) => {
                e.stopPropagation();
                removeChip(chip);
              }}
              className="ml-1 text-indigo-400 hover:text-indigo-700"
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setActiveIndex(0);
            setIsOpen(e.target.value.trim() !== '');
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (text.trim() !== '') setIsOpen(true);
          }}
          onBlur={() => {
            // Delayed so a click on a suggestion row registers before the
            // dropdown unmounts.
            setTimeout(() => setIsOpen(false), 100);
          }}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className={`flex-1 min-w-[80px] outline-none bg-transparent text-slate-700 placeholder-slate-400 ${compact ? 'text-xs' : 'text-sm'}`}
        />
      </div>
      {showDropdown && (
        <ul
          data-testid="refine-chip-suggestions"
          className="absolute z-10 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg"
        >
          {suggestions.length === 0 ? (
            <li className="px-3 py-1.5 text-sm text-slate-400">No matches in these results</li>
          ) : (
            suggestions.map((s, idx) => (
              <li key={`${s.kind}-${s.value}`}>
                <button
                  type="button"
                  data-testid="refine-chip-suggestion"
                  // Out of the tab order: keyboard users move with the arrow
                  // keys from the input, and Tab leaves the control cleanly.
                  tabIndex={-1}
                  aria-current={idx === active ? 'true' : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addChip(s)}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 flex items-center justify-between gap-2 ${idx === active ? 'bg-indigo-50' : ''}`}
                >
                  <span>
                    <span className="text-slate-400 mr-1.5">{REFINE_KIND_LABELS[s.kind]}</span>
                    {s.kind === 'text' ? `"${s.value}"` : formatRefineChipValue(s, usFdaVisible)}
                  </span>
                  <span className="text-slate-400 text-xs">{s.count}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default RefineChipBar;
